/**
 * Web push (FCM) registration.
 *
 * Call `enableWebPush()` once after the user signs in. It:
 *   1. Registers the service worker we ship in /firebase-messaging-sw.js.
 *   2. Asks the browser for notification permission (user can deny).
 *   3. Gets a token from FCM.
 *   4. Registers it with the API at POST /api/v1/me/fcm-tokens.
 *
 * Calling more than once is a no-op (idempotent token registration on the
 * server side).
 *
 * If the browser doesn't support service workers (older Safari, etc.) or
 * the user denies permission, this resolves quietly without throwing.
 *
 * The VAPID public key (used for web push) is configured in the Firebase
 * Console → Cloud Messaging → Web Push certificates. Set
 * `VITE_FCM_VAPID_KEY` in the build env to wire it.
 */

import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";

import { app } from "../firebase.config";
import { api } from "../api/client";

let registered = false;

export async function enableWebPush(): Promise<{ ok: boolean; reason?: string }> {
  if (registered) return { ok: true };

  if (!(await isSupported())) {
    return { ok: false, reason: "Browser doesn't support web push" };
  }
  if (!("serviceWorker" in navigator)) {
    return { ok: false, reason: "No service worker support" };
  }
  if (Notification.permission === "denied") {
    return { ok: false, reason: "Notifications blocked by user" };
  }

  // Request permission if not already granted
  let permission: NotificationPermission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") {
    return { ok: false, reason: "Permission not granted" };
  }

  try {
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" });
    const messaging = getMessaging(app);

    // VAPID key — public; safe to ship. Empty falls back to sender-only auth (still works for FCM).
    const vapidKey = import.meta.env.VITE_FCM_VAPID_KEY;
    const token = await getToken(messaging, {
      vapidKey: vapidKey || undefined,
      serviceWorkerRegistration: reg,
    });
    if (!token) {
      return { ok: false, reason: "FCM returned no token" };
    }

    await api("/me/fcm-tokens", {
      method: "POST",
      body: JSON.stringify({ token, platform: "web" }),
    });

    // Foreground messages — the SW only handles background. When the tab is
    // open we display them ourselves (a small toast would be nicer; for now
    // we log + the polling-based badge already updates).
    onMessage(messaging, (payload) => {
      console.info("[fcm] foreground push", payload);
    });

    registered = true;
    return { ok: true };
  } catch (err) {
    console.warn("[fcm] registration failed", err);
    return { ok: false, reason: (err as Error).message };
  }
}
